import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

/**
 * Simplified useSelect hook implementation based on downshift v6 pattern
 * @param {Object} options - Configuration options
 * @param {string} [options.id] - Optional ID for the select
 * @param {Array<string | null | undefined>} options.items - Array of items
 * @param {string | null | undefined} options.selectedItem - Currently selected item
 * @param {function({selectedItem: string | null | undefined}): void} options.onSelectedItemChange - Callback when selection changes
 * @returns {Object} Select state and prop getters
 */
const useSelect = ({ id, items, selectedItem, onSelectedItemChange }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const toggleButtonRef = useRef(null);
    const menuRef = useRef(null);
    const itemRefs = useRef([]);

    // Generate IDs
    const buttonId = id ? `${id}-button` : undefined;
    const menuId = id ? `${id}-menu` : undefined;

    // Handle toggle button click
    const handleToggleClick = useCallback(() => {
        setIsOpen((prev) => !prev);
        if (!isOpen) {
            // Find index of selected item when opening
            const selectedIndex = items.indexOf(selectedItem);
            setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }
    }, [isOpen, items, selectedItem]);

    // Handle item click
    const handleItemClick = useCallback(
        (item) => {
            onSelectedItemChange?.({ selectedItem: item });
            setIsOpen(false);
            setHighlightedIndex(-1);
            toggleButtonRef.current?.focus();
        },
        [onSelectedItemChange],
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
        (event) => {
            if (
                !isOpen &&
                (event.key === 'ArrowDown' ||
                    event.key === 'ArrowUp' ||
                    event.key === 'Enter' ||
                    event.key === ' ')
            ) {
                event.preventDefault();
                setIsOpen(true);
                const selectedIndex = items.indexOf(selectedItem);
                setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
                return;
            }

            if (!isOpen) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setHighlightedIndex((prev) =>
                        prev < items.length - 1 ? prev + 1 : prev,
                    );
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                    break;
                case 'Home':
                    event.preventDefault();
                    setHighlightedIndex(0);
                    break;
                case 'End':
                    event.preventDefault();
                    setHighlightedIndex(items.length - 1);
                    break;
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    if (
                        highlightedIndex >= 0 &&
                        highlightedIndex < items.length
                    ) {
                        handleItemClick(items[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                    toggleButtonRef.current?.focus();
                    break;
                default:
                    break;
            }
        },
        [isOpen, items, highlightedIndex, selectedItem, handleItemClick],
    );

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isOpen &&
                toggleButtonRef.current &&
                menuRef.current &&
                !toggleButtonRef.current.contains(event.target) &&
                !menuRef.current.contains(event.target)
            ) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () =>
                document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (
            isOpen &&
            highlightedIndex >= 0 &&
            itemRefs.current[highlightedIndex]
        ) {
            itemRefs.current[highlightedIndex].scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    }, [highlightedIndex, isOpen]);

    const getToggleButtonProps = useCallback(
        () => ({
            ref: toggleButtonRef,
            id: buttonId,
            'aria-haspopup': 'listbox',
            'aria-expanded': isOpen,
            'aria-labelledby': buttonId,
            onClick: handleToggleClick,
            onKeyDown: handleKeyDown,
            type: 'button',
        }),
        [buttonId, isOpen, handleToggleClick, handleKeyDown],
    );

    const getMenuProps = useCallback(
        () => ({
            ref: menuRef,
            id: menuId,
            role: 'listbox',
            'aria-labelledby': buttonId,
            onKeyDown: handleKeyDown,
        }),
        [menuId, buttonId, handleKeyDown],
    );

    const getItemProps = useCallback(
        ({ item, index }) => ({
            ref: (el) => {
                itemRefs.current[index] = el;
            },
            role: 'option',
            'aria-selected': item === selectedItem,
            'data-highlighted': index === highlightedIndex,
            onClick: () => handleItemClick(item),
            onMouseEnter: () => setHighlightedIndex(index),
            style: {
                backgroundColor:
                    index === highlightedIndex
                        ? 'var(--chakra-colors-brand-500)'
                        : darkMode
                          ? 'var(--chakra-colors-dark-tcap-weak)'
                          : undefined,
                color: index === highlightedIndex ? '#ffffff' : undefined,
                cursor: 'pointer',
            },
        }),
        [darkMode, selectedItem, highlightedIndex, handleItemClick],
    );

    return {
        isOpen,
        selectedItem,
        highlightedIndex,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
    };
};

export { useSelect };